/**
 * Company and Project Context Provider
 * Manages current company and project selection
 */
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import * as companiesApi from '../services/companiesApi';
import * as projectsApi from '../services/projectsApi';
import { Company } from '../types/company';
import { Project } from '../types/project';
import { useAuth } from './AuthProvider';

interface CompanyProjectContextType {
  // State
  currentCompany: Company | null;
  currentProject: Project | null;
  companies: Company[];
  projects: Project[];
  loading: boolean;
  error: string | null;

  // Actions
  setCurrentCompany: (companyId: number) => Promise<void>;
  setCurrentProject: (projectId: number) => Promise<void>;
  loadCompanies: () => Promise<void>;
  loadProjects: (companyId?: number) => Promise<void>;
  refreshCompanies: () => Promise<void>;
  refreshProjects: () => Promise<void>;

  // Helpers
  isCompanySelectorDisabled: boolean;
  isProjectSelectorDisabled: boolean;
}

const CompanyProjectContext = createContext<CompanyProjectContextType | undefined>(undefined);

export const useCompanyProject = () => {
  const context = useContext(CompanyProjectContext);
  if (!context) {
    throw new Error('useCompanyProject must be used within CompanyProjectProvider');
  }
  return context;
};

interface Props {
  children: ReactNode;
}

export const CompanyProjectProvider: React.FC<Props> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [currentCompany, setCurrentCompanyState] = useState<Company | null>(null);
  const [currentProject, setCurrentProjectState] = useState<Project | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadCompanies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const companiesList = await companiesApi.listCompanies();
      setCompanies(companiesList);

      // Auto-select company from localStorage or first company
      const savedCompanyId = localStorage.getItem('current_company_id');
      if (savedCompanyId) {
        const savedCompany = companiesList.find(c => c.id === parseInt(savedCompanyId));
        if (savedCompany) {
          setCurrentCompanyState(savedCompany);
        } else if (companiesList.length > 0) {
          // Saved company not found, select first
          setCurrentCompanyState(companiesList[0]);
          localStorage.setItem('current_company_id', companiesList[0].id.toString());
        }
      } else if (companiesList.length > 0) {
        // No saved company, select first
        setCurrentCompanyState(companiesList[0]);
        localStorage.setItem('current_company_id', companiesList[0].id.toString());
      }
    } catch (err) {
      console.error('Error loading companies:', err);
      setError('Failed to load companies');
      // Clear state on error
      setCompanies([]);
      setCurrentCompanyState(null);
      setCurrentProjectState(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadProjects = useCallback(async (companyId?: number) => {
    try {
      setLoading(true);
      setError(null);

      const targetCompanyId = companyId || currentCompany?.id;
      if (!targetCompanyId) {
        setProjects([]);
        setCurrentProjectState(null);
        return;
      }

      const projectsList = await projectsApi.listProjectsByCompany(targetCompanyId);
      setProjects(projectsList);

      // Auto-select project from localStorage or first project
      const savedProjectId = localStorage.getItem('current_project_id');
      if (savedProjectId) {
        const savedProject = projectsList.find(p => p.id === parseInt(savedProjectId));
        if (savedProject) {
          setCurrentProjectState(savedProject);
        } else if (projectsList.length > 0) {
          // Saved project not found, select first
          setCurrentProjectState(projectsList[0]);
          localStorage.setItem('current_project_id', projectsList[0].id.toString());
        }
      } else if (projectsList.length > 0) {
        // No saved project, select first
        setCurrentProjectState(projectsList[0]);
        localStorage.setItem('current_project_id', projectsList[0].id.toString());
      } else {
        // No projects available
        setCurrentProjectState(null);
        localStorage.removeItem('current_project_id');
      }
    } catch (err) {
      console.error('Error loading projects:', err);
      setError('Failed to load projects');
      setProjects([]);
      setCurrentProjectState(null);
    } finally {
      setLoading(false);
    }
  }, [currentCompany]);

  // Load companies only when authenticated
  useEffect(() => {
    if (!authLoading) {
      if (isAuthenticated) {
        loadCompanies();
      } else {
        // Clear state when not authenticated
        setCurrentCompanyState(null);
        setCurrentProjectState(null);
        setCompanies([]);
        setProjects([]);
        setLoading(false);
      }
    }
  }, [isAuthenticated, authLoading, loadCompanies]);

  // Auto-load projects when company changes
  useEffect(() => {
    if (currentCompany) {
      loadProjects(currentCompany.id);
    }
  }, [currentCompany, loadProjects]);

  const setCurrentCompany = async (companyId: number) => {
    const company = companies.find(c => c.id === companyId);
    if (company) {
      setCurrentCompanyState(company);
      localStorage.setItem('current_company_id', companyId.toString());

      // Clear current project when switching companies
      setCurrentProjectState(null);
      localStorage.removeItem('current_project_id');

      // Load projects for new company
      await loadProjects(companyId);
    }
  };

  const setCurrentProject = async (projectId: number) => {
    const project = projects.find(p => p.id === projectId);
    if (project) {
      setCurrentProjectState(project);
      localStorage.setItem('current_project_id', projectId.toString());
    }
  };

  const refreshCompanies = async () => {
    await loadCompanies();
  };

  const refreshProjects = async () => {
    if (currentCompany) {
      await loadProjects(currentCompany.id);
    }
  };

  // Disable selectors if only one option
  const isCompanySelectorDisabled = companies.length <= 1;
  const isProjectSelectorDisabled = projects.length <= 1;

  const value: CompanyProjectContextType = {
    currentCompany,
    currentProject,
    companies,
    projects,
    loading,
    error,
    setCurrentCompany,
    setCurrentProject,
    loadCompanies,
    loadProjects,
    refreshCompanies,
    refreshProjects,
    isCompanySelectorDisabled,
    isProjectSelectorDisabled,
  };

  return (
    <CompanyProjectContext.Provider value={value}>
      {children}
    </CompanyProjectContext.Provider>
  );
};
